'use client';

import { useState, useEffect } from 'react';
import ResumeHeader from './ResumeHeader';
import ContactInfo from './ContactInfo';
import ProfessionalSummary from './ProfessionalSummary';
import ExperienceTimeline from './ExperienceTimeline';
import EducationSection from './EducationSection';
import SkillsMatrix from './SkillsMatrix';
import ProjectHighlights from './ProjectHighlights';
import AvailabilityStatus from './AvailabilityStatus';

/* =======================
   Types
======================= */

interface ContactItem {
  icon: string;
  label: string;
  value: string;
  link?: string;
}

interface Experience {
  id: number;
  company: string;
  position: string;
  duration: string;
  location: string;
  type: string;
  responsibilities: string[];
  achievements: string[];
  technologies: string[];
}

interface Education {
  id: number;
  degree: string;
  institution: string;
  duration: string;
  location: string;
  relevantCourses: string[];
}

interface Skill {
  category: string;
  skills: {
    name: string;
    level: number;
    years: number;
  }[];
}

interface Project {
  id: number;
  title: string;
  description: string;
  role: string;
  impact: string;
  technologies: string[];
}

/* =======================
   Component
======================= */

export default function ResumeInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  /* =======================
     Contact Info
  ======================= */

  const contactInfo: ContactItem[] = [
    {
      icon: 'EnvelopeIcon',
      label: 'Email',
      value: 'obudho200@gmail.com',
      link: 'mailto:obudho200@gmail.com'
    },
    {
      icon: 'PhoneIcon',
      label: 'Phone',
      value: '+254 725 745 754',
      link: 'tel:+254725745754'
    },
    {
      icon: 'MapPinIcon',
      label: 'Location',
      value: 'Nairobi, Kenya'
    },
    {
      icon: 'CodeBracketIcon',
      label: 'GitHub',
      value: 'github.com/Michael-juma',
      link: 'https://github.com/Michael-juma'
    },
    {
      icon: 'UserGroupIcon',
      label: 'LinkedIn',
      value: 'linkedin.com/in/michael-juma-obudho-497190364',
      link: 'https://www.linkedin.com/in/michael-juma-obudho-497190364/'
    }
  ];

  /* =======================
     Professional Summary
  ======================= */

  const professionalSummary = {
    summary:
      'Full-Stack Software Engineer trained at Moringa School with strong hands-on experience building modern web applications using React, Flask, and PostgreSQL. Passionate about clean architecture, RESTful APIs, and user-centered design. Experienced in delivering end-to-end projects including authentication, dashboards, and data-driven systems.',
    highlights: [
      'Built multiple full-stack applications using React and Flask',
      'Designed RESTful APIs with SQLAlchemy and PostgreSQL',
      'Implemented authentication and role-based access control',
      'Strong understanding of CRUD operations and relational databases',
      'Collaborated on team projects using Git and GitHub'
    ]
  };

  /* =======================
     Experience (Project-Based)
  ======================= */

  const experiences: Experience[] = [
    {
      id: 1,
      company: 'Moringa School',
      position: 'Full-Stack Software Engineering Trainee',
      duration: '2025 – 2026',
      location: 'Nairobi, Kenya',
      type: 'Training & Projects',
      responsibilities: [
        'Developed full-stack web applications using React and Flask',
        'Built RESTful APIs following MVC architecture',
        'Implemented authentication and authorization using JWT',
        'Designed relational databases with SQLAlchemy',
        'Worked in Agile teams using GitHub for version control'
      ],
      achievements: [
        'Completed multiple production-ready full-stack projects',
        'Built secure CRUD applications from scratch',
        'Applied clean code and REST best practices',
        'Collaborated successfully in team-based projects'
      ],
      technologies: [
        'React',
        'JavaScript',
        'Python',
        'Flask',
        'SQLAlchemy',
        'PostgreSQL',
        'HTML',
        'CSS',
        'Git'
      ]
    }
  ];

  /* =======================
     Education
  ======================= */

  const education: Education[] = [
    {
      id: 1,
      degree: 'Certificate in Software Engineering',
      institution: 'Moringa School',
      duration: '2025 – 2026',
      location: 'Ngong Road, Nairobi, Kenya',
      relevantCourses: [
        'JavaScript & React Development',
        'Python & Flask APIs',
        'SQLAlchemy & PostgreSQL',
        'Data Structures & Algorithms',
        'Software Engineering Principles',
        'Agile Development'
      ]
    }
  ];

  /* =======================
     Skills
  ======================= */

  const skillCategories: Skill[] = [
    {
      category: 'Frontend Development',
      skills: [
        { name: 'React.js', level: 90, years: 1 },
        { name: 'JavaScript (ES6+)', level: 90, years: 1 },
        { name: 'HTML5 & CSS3', level: 90, years: 1 },
        { name: 'Tailwind CSS', level: 85, years: 1 }
      ]
    },
    {
      category: 'Backend Development',
      skills: [
        { name: 'Python', level: 85, years: 1 },
        { name: 'Flask', level: 85, years: 1 },
        { name: 'SQLAlchemy', level: 80, years: 1 },
        { name: 'RESTful APIs', level: 85, years: 1 }
      ]
    },
    {
      category: 'Database & Tools',
      skills: [
        { name: 'PostgreSQL', level: 80, years: 1 },
        { name: 'SQLite', level: 80, years: 1 },
        { name: 'Git & GitHub', level: 90, years: 1 },
        { name: 'Agile / Scrum', level: 80, years: 1 }
      ]
    }
  ];

  /* =======================
     Projects
  ======================= */

  const featuredProjects: Project[] = [
    {
      id: 1,
      title: 'Food Court Web Application',
      description:
        'A full-stack food ordering platform where customers browse menus, place orders, and restaurant owners manage menus and orders.',
      role: 'Full-Stack Developer',
      impact:
        'Implemented role-based authentication and cart-based ordering system',
      technologies: ['React', 'Flask', 'PostgreSQL', 'JWT', 'SQLAlchemy']
    },
    {
      id: 2,
      title: 'Livestock Disease Management System',
      description:
        'A research-focused platform for managing livestock and fish disease data including symptoms, treatments, and prevention.',
      role: 'Full-Stack Developer',
      impact:
        'Designed relational models and built searchable disease records',
      technologies: ['React', 'Flask', 'SQLAlchemy', 'PostgreSQL']
    },
    {
      id: 3,
      title: 'SalesPilot',
      description:
        'A sales management dashboard for tracking customers, products, and performance metrics.',
      role: 'Full-Stack Developer',
      impact:
        'Built REST APIs and interactive dashboards for data visualization',
      technologies: ['React', 'Flask', 'Python', 'PostgreSQL']
    }
  ];

  /* =======================
     Download Handler
  ======================= */

  const handleDownload = (format: 'pdf' | 'word' | 'txt') => {
    alert(`Preparing ${format.toUpperCase()} resume for download...`);
  };

  /* =======================
     Render
  ======================= */

  if (!isHydrated) {
    return (
      <div data-component-id="app/resume/components/ResumeInteractive.tsx:272:6" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="272" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-background flex items-center justify-center">
        <p data-component-id="app/resume/components/ResumeInteractive.tsx:273:8" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="273" data-component-file="ResumeInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Loading%20resume...%22%7D" className="text-muted-foreground">Loading resume...</p>
      </div>
    );
  }

  return (
    <div data-component-id="app/resume/components/ResumeInteractive.tsx:279:4" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="279" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <ResumeHeader data-component-id="app/resume/components/ResumeInteractive.tsx:280:6" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="280" data-component-file="ResumeInteractive.tsx" data-component-name="ResumeHeader" data-component-content="%7B%22elementName%22%3A%22ResumeHeader%22%7D" onDownload={handleDownload} />

      <div data-component-id="app/resume/components/ResumeInteractive.tsx:282:6" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="282" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div data-component-id="app/resume/components/ResumeInteractive.tsx:283:8" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="283" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div data-component-id="app/resume/components/ResumeInteractive.tsx:284:10" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="284" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-8%22%7D" className="lg:col-span-2 space-y-8">
            <ProfessionalSummary data-component-id="app/resume/components/ResumeInteractive.tsx:285:12" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="285" data-component-file="ResumeInteractive.tsx" data-component-name="ProfessionalSummary" data-component-content="%7B%22elementName%22%3A%22ProfessionalSummary%22%7D"
              summary={professionalSummary.summary}
              highlights={professionalSummary.highlights}
            />
            <ExperienceTimeline data-component-id="app/resume/components/ResumeInteractive.tsx:289:12" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="289" data-component-file="ResumeInteractive.tsx" data-component-name="ExperienceTimeline" data-component-content="%7B%22elementName%22%3A%22ExperienceTimeline%22%7D" experiences={experiences} />
            <ProjectHighlights data-component-id="app/resume/components/ResumeInteractive.tsx:290:12" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="290" data-component-file="ResumeInteractive.tsx" data-component-name="ProjectHighlights" data-component-content="%7B%22elementName%22%3A%22ProjectHighlights%22%7D" projects={featuredProjects} />
          </div>

          <div data-component-id="app/resume/components/ResumeInteractive.tsx:293:10" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="293" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-8%22%7D" className="space-y-8">
            <AvailabilityStatus data-component-id="app/resume/components/ResumeInteractive.tsx:294:12" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="294" data-component-file="ResumeInteractive.tsx" data-component-name="AvailabilityStatus" data-component-content="%7B%22elementName%22%3A%22AvailabilityStatus%22%7D" status="available" responseTime="24–48 hours" />
            <ContactInfo data-component-id="app/resume/components/ResumeInteractive.tsx:295:12" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="295" data-component-file="ResumeInteractive.tsx" data-component-name="ContactInfo" data-component-content="%7B%22elementName%22%3A%22ContactInfo%22%7D" contacts={contactInfo} />
            <EducationSection data-component-id="app/resume/components/ResumeInteractive.tsx:296:12" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="296" data-component-file="ResumeInteractive.tsx" data-component-name="EducationSection" data-component-content="%7B%22elementName%22%3A%22EducationSection%22%7D" education={education} />
          </div>
        </div>

        <div data-component-id="app/resume/components/ResumeInteractive.tsx:300:8" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="300" data-component-file="ResumeInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-8%22%7D" className="mt-8">
          <SkillsMatrix data-component-id="app/resume/components/ResumeInteractive.tsx:301:10" data-component-path="app/resume/components/ResumeInteractive.tsx" data-component-line="301" data-component-file="ResumeInteractive.tsx" data-component-name="SkillsMatrix" data-component-content="%7B%22elementName%22%3A%22SkillsMatrix%22%7D" skillCategories={skillCategories} />
        </div>
      </div>
    </div>
  );
}
